import { SANDBOX_ERROR_CODES, type SandboxErrorCode, type SandboxErrorData } from "./types";

/**
 * A refused or failed sandbox operation. File-system codes lead the message the way node's do
 * ("EROFS: read-only file system, write '/context/a.md'"), which is what just-bash's commands print.
 */
export class SandboxError extends Error {
  constructor(
    readonly code: SandboxErrorCode,
    message: string
  ) {
    super(message);
    this.name = "SandboxError";
  }

  toData(): SandboxErrorData {
    return { code: this.code, message: this.message };
  }
}

const FS_TEXT: Partial<Record<SandboxErrorCode, string>> = {
  EROFS: "read-only file system",
  EACCES: "permission denied",
  ENOENT: "no such file or directory",
  EISDIR: "illegal operation on a directory",
  ENOTDIR: "not a directory",
  EEXIST: "file already exists",
  ENOTEMPTY: "directory not empty",
  EINVAL: "invalid argument",
  ENOSPC: "no space left on device",
};

export function fsError(
  code: SandboxErrorCode,
  op: string,
  path: string,
  detail?: string
): SandboxError {
  const text = FS_TEXT[code] ?? code.toLowerCase();
  return new SandboxError(code, `${code}: ${text}, ${op} '${path}'${detail ? ` (${detail})` : ""}`);
}

/** Hosts browsers treat as potentially trustworthy: an http link to them is not mixed content. */
function isLoopback(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname.endsWith(".localhost") ||
    hostname === "[::1]" ||
    /^127(\.\d{1,3}){3}$/.test(hostname)
  );
}

/**
 * A fetch of `url` that failed with no HTTP status: the browser refused it or the network failed.
 * An http link on an https page is mixed content, which the browser blocks; only the link's server
 * can fix that, so the message says so instead of passing on "Load failed". It names the scheme
 * and host alone: the rest of a link may hold a signed token. `pageProtocol` is the page's own
 * (location.protocol where the fetch runs), never a sandboxed worker's.
 * An https link the server redirects to http still reads "Load failed": fetch cannot see that hop.
 */
export function fetchFailure(
  url: URL,
  pageProtocol: string | undefined,
  error: unknown
): SandboxError {
  if (pageProtocol === "https:" && url.protocol === "http:" && !isLoopback(url.hostname)) {
    return new SandboxError(
      "EFETCH",
      `EFETCH: its link (http://${url.host}) is http:// on an https page, so the browser blocked it (mixed content). ` +
        "This is a link problem on the server side; a new sandbox will not fix it."
    );
  }
  return new SandboxError(
    "EFETCH",
    `EFETCH: ${error instanceof Error ? error.message : "network error"}`
  );
}

const CODES = new Set<string>(SANDBOX_ERROR_CODES);

/** Any thrown value as port-safe data; unknown errors keep only their message. */
export function toErrorData(error: unknown): SandboxErrorData {
  if (error instanceof SandboxError) return error.toData();
  const message = error instanceof Error ? error.message : String(error);
  const lead = /^([A-Z]+):/.exec(message)?.[1];
  return { code: lead && CODES.has(lead) ? (lead as SandboxErrorCode) : "INTERNAL", message };
}

/** Error data that arrived over a port, which is untrusted: only known codes survive. */
export function fromErrorData(data: unknown): SandboxError {
  const value = (
    typeof data === "object" && data !== null ? data : {}
  ) as Partial<SandboxErrorData>;
  const code = typeof value.code === "string" && CODES.has(value.code) ? value.code : "INTERNAL";
  const message =
    typeof value.message === "string" ? value.message.slice(0, 4000) : "sandbox error";
  return new SandboxError(code, message);
}
